import { EditOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Checkbox, Form, Input, Modal, Space, Typography } from "antd";
import type { TableProps } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { compareNumeric } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  ListResponse,
  NotificationsResponse,
  SpeakerSpeechSelectionRow,
} from "../api/types";
import { DataTable } from "../components/DataTable";

/**
 * Sidebar entry "speaker_speech_selection" → /speech/speech_selection.
 *
 * Which talks the speaker offers, maintained by the speaker themselves. The
 * same list the local coordinator sees on /speaker/manage, and the same rule
 * decides what is on it: the talks in the circumscription's language, plus
 * whatever the speaker's collaborations open up, plus the ones already selected.
 *
 * Selecting and editing the notes is one endpoint — it upserts the
 * speaker_speeches row — which is why the notes can be changed without
 * deselecting first.
 */
export function SpeechSelectionPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [notesForm] = Form.useForm<{ additional_notes: string }>();

  const [search, setSearch] = useState("");
  const [editingNotes, setEditingNotes] = useState<SpeakerSpeechSelectionRow | null>(null);

  const queryKey = useMemo(() => ["speeches", "selection"], []);

  const speeches = useQuery({
    queryKey,
    queryFn: () => apiFetch<ListResponse<SpeakerSpeechSelectionRow>>("/speeches/selection"),
  });

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const refresh = useCallback(
    (data: NotificationsResponse) => {
      notify(data.notifications);
      void queryClient.invalidateQueries({ queryKey });
    },
    [notify, queryClient, queryKey],
  );

  const select = useMutation({
    mutationFn: ({ speechId, notes }: { speechId: number; notes?: string | null }) =>
      apiFetch<NotificationsResponse>(`/speeches/${speechId}/select`, {
        method: "PUT",
        body: { additional_notes: notes ?? null },
      }),
    onSuccess: (data) => {
      setEditingNotes(null);
      refresh(data);
    },
    onError: showError,
  });

  const deselect = useMutation({
    mutationFn: (speechId: number) =>
      apiFetch<NotificationsResponse>(`/speeches/${speechId}/select`, { method: "DELETE" }),
    onSuccess: refresh,
    onError: showError,
  });

  const busy = select.isPending || deselect.isPending;

  // Filtered here rather than on the server: the endpoint answers with the
  // speaker's whole list at once, and the old grid searched the loaded rows too
  // (#text_filter over exactly these columns).
  const rows = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    const all = speeches.data?.data ?? [];
    if (!needle) return all;

    return all.filter((row) =>
      [row.schema, row.title, row.language_code, row.additional_notes].some((field) =>
        (field ?? "").toLocaleLowerCase().includes(needle),
      ),
    );
  }, [speeches.data, search]);

  const columns = useMemo<TableProps<SpeakerSpeechSelectionRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.speaker_speech.selected"),
        dataIndex: "selected",
        width: 90,
        render: (selected: boolean, row) => (
          <Checkbox
            checked={selected}
            disabled={busy}
            onChange={(event) =>
              event.target.checked
                ? select.mutate({ speechId: row.id, notes: row.additional_notes })
                : deselect.mutate(row.id)
            }
          />
        ),
      },
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "schema",
        width: 100,
        align: "right",
        sorter: (a, b) => compareNumeric(a.schema, b.schema),
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "title",
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.speech.language_id"),
        dataIndex: "language_code",
        width: 100,
      },
      {
        title: t("activerecord.attributes.speaker_speech.additional_notes"),
        dataIndex: "additional_notes",
        width: 300,
        ellipsis: true,
        render: (notes: string | null, row) =>
          // Notes belong to the selection, so there is nothing to write them to
          // until the talk is selected.
          row.selected ? (
            <Space>
              <Typography.Text ellipsis>{notes}</Typography.Text>
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={busy}
                onClick={() => {
                  setEditingNotes(row);
                  notesForm.resetFields();
                  notesForm.setFieldsValue({ additional_notes: notes ?? "" });
                }}
              />
            </Space>
          ) : null,
      },
    ],
    [t, busy, select, deselect, notesForm],
  );

  return (
    <Card title={t("navigation.public_talks")}>
      <Input.Search
        allowClear
        placeholder={t("filter.search")}
        onChange={(event) => setSearch(event.target.value)}
        style={{ width: 320, marginBottom: 16 }}
      />

      <DataTable<SpeakerSpeechSelectionRow>
        columns={columns}
        rows={rows}
        exportAs={t("navigation.public_talks")}
        loading={speeches.isPending || busy}
        error={speeches.error}
      />

      <Modal
        open={editingNotes !== null}
        title={t("activerecord.attributes.speaker_speech.additional_notes")}
        onCancel={() => setEditingNotes(null)}
        okText={t("form.update")}
        okButtonProps={{ loading: select.isPending }}
        onOk={() => notesForm.submit()}
        destroyOnHidden
      >
        <Form
          form={notesForm}
          layout="vertical"
          onFinish={({ additional_notes: notes }) =>
            select.mutate({ speechId: editingNotes!.id, notes })
          }
        >
          <Typography.Paragraph type="secondary">
            {[editingNotes?.schema, editingNotes?.title].filter(Boolean).join(" – ")}
          </Typography.Paragraph>

          <Form.Item name="additional_notes">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
